import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StoreList = ({ clientId, villageCode, storeGroups, onEditStore, onAddStore }) => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Build storeId -> [group names] lookup from storeGroups
  const storeGroupMap = React.useMemo(() => {
    const map = {};
    (storeGroups || []).forEach(group => {
      (group.store_ids || []).forEach(storeId => {
        if (!map[storeId]) map[storeId] = [];
        map[storeId].push({ id: group.id, name: group.name });
      });
    });
    return map;
  }, [storeGroups]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchStores();
  }, [clientId, page, groupFilter, statusFilter]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      setPage(1);
      fetchStores();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({ page, limit });
      if (searchQuery) params.append('search', searchQuery);
      if (groupFilter) params.append('group', groupFilter);
      if (statusFilter !== '') params.append('is_active', statusFilter);
      
      const response = await fetch(
        `${API_URL}/api/admin/unified-clients/${clientId}/stores?${params}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch stores');
      
      const data = await response.json();
      setStores(data.stores);
      setTotal(data.total);
      setTotalPages(data.pages);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow" data-testid="store-list">
      {/* Header with Add Button */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Stores</h3>
        {villageCode && (
          <button
            onClick={onAddStore}
            className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="add-store-button"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Store
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="store-search"
            />
          </div>
          
          {/* Group Filter */}
          <select
            value={groupFilter}
            onChange={(e) => { setGroupFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="store-group-filter"
          >
            <option value="">All Groups</option>
            {storeGroups.map(group => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
          
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="store-status-filter"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        
        <div className="mt-2 text-sm text-gray-500">
          {total} store{total !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 text-red-600">{error}</div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Segment</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Groups</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : stores.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  No stores found
                </td>
              </tr>
            ) : (
              stores.map(store => (
                <tr key={store.id} className="hover:bg-gray-50" data-testid={`store-row-${store.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{store.name}</div>
                    <div className="text-xs text-gray-500">{store.id}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{store.type || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{store.category || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{store.segment || '—'}</td>
                  <td className="px-4 py-3">
                    {(storeGroupMap[store.id] || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {storeGroupMap[store.id].map(g => (
                          <span
                            key={g.id}
                            className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                            data-testid={`store-group-chip-${store.id}-${g.id}`}
                          >
                            {g.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      store.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {store.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onEditStore(store)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      data-testid={`edit-store-${store.id}`}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              data-testid="pagination-prev"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              data-testid="pagination-next"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreList;
